import { useEffect, useState } from 'react';
import { BrandInput } from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { Button } from '@Components/Button';
import { Offcanvas } from '@Components/Offcanvas';
import { useSetError } from '../../../redux/modals';
import { getAccountBrand, updateAccountBrand } from '../google-ads-audit-api';
import { BrandProfileFields } from './BrandProfileFields';

const EMPTY_FORM: BrandInput = { displayName: '' };

interface BrandConfigDrawerProps {
  company: string;
  accountId: string;
  accountName: string;
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const BrandConfigDrawer = ({
  company,
  accountId,
  accountName,
  show,
  onClose,
  onSaved
}: BrandConfigDrawerProps) => {
  const [form, setForm] = useState<BrandInput>(EMPTY_FORM);
  const [marketsText, setMarketsText] = useState('');
  const [contactsText, setContactsText] = useState('[]');
  const [saving, setSaving] = useState(false);
  const setError = useSetError();

  useEffect(() => {
    if (!show) return;
    getAccountBrand(company, accountId, accountName)
      .then(({ brand }) => {
        setForm(brand);
        setMarketsText((brand.markets ?? []).join(', '));
        setContactsText(JSON.stringify(brand.clientContacts ?? [], null, 2));
      })
      .catch(e => setError({ message: e.message, reportError: e }));
  }, [show, company, accountId, accountName, setError]);

  const save = async () => {
    let clientContacts: BrandInput['clientContacts'];
    try {
      clientContacts = JSON.parse(contactsText || '[]');
    } catch (e) {
      setError({ message: 'Client contacts must be valid JSON.', reportError: e });
      return;
    }
    setSaving(true);
    try {
      await updateAccountBrand(company, accountId, {
        ...form,
        markets: marketsText
          .split(',')
          .map(m => m.trim())
          .filter(Boolean),
        clientContacts
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      header={<span>Configure {accountName}</span>}
      width='480px'
    >
      <BrandProfileFields
        form={form}
        setForm={setForm}
        marketsText={marketsText}
        setMarketsText={setMarketsText}
        contactsText={contactsText}
        setContactsText={setContactsText}
      />
      <Button onClick={save} loading={saving}>
        Save
      </Button>
    </Offcanvas>
  );
};
